import type { PatternProject } from '../types';

export interface FillResult {
  project: PatternProject;
  count: number;
}

/** Fill a four-directionally connected region without mutating the source project. */
export function fillContiguousArea(project: PatternProject, startIndex: number, targetColor: number): FillResult {
  const cellCount = project.width * project.height;
  if (startIndex < 0 || startIndex >= cellCount || targetColor < 0) return { project, count: 0 };

  const sourceEmpty = project.empty[startIndex] === 1;
  const sourceColor = project.colorIndices[startIndex];
  if (!sourceEmpty && sourceColor === targetColor) return { project, count: 0 };

  const colorIndices = project.colorIndices.slice();
  const empty = project.empty.slice();
  const completed = project.completed.slice();
  const queue = new Uint32Array(cellCount);
  let head = 0;
  let tail = 0;

  const matchesSource = (index: number) => sourceEmpty
    ? empty[index] === 1
    : empty[index] === 0 && colorIndices[index] === sourceColor;
  const enqueue = (index: number) => {
    colorIndices[index] = targetColor;
    empty[index] = 0;
    completed[index] = 0;
    queue[tail++] = index;
  };

  enqueue(startIndex);
  while (head < tail) {
    const index = queue[head++];
    const col = index % project.width;
    if (col > 0 && matchesSource(index - 1)) enqueue(index - 1);
    if (col + 1 < project.width && matchesSource(index + 1)) enqueue(index + 1);
    if (index >= project.width && matchesSource(index - project.width)) enqueue(index - project.width);
    if (index + project.width < cellCount && matchesSource(index + project.width)) enqueue(index + project.width);
  }

  return {
    project: { ...project, colorIndices, empty, completed, updatedAt: Date.now() },
    count: tail
  };
}
